import 'normalize.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import React from 'react'
import 'react-toastify/dist/ReactToastify.css'
import { ErrorBoundary } from 'react-error-boundary'
import { ThemeProvider } from 'styled-components'
import theme from './design'
import './design/index.scss'
import entities from './packages/entities'
import actionDefinitions from './packages/actionDefinitions'
import Router from './packages/routes'
import Handle from './prix/react/components/handle'
import createServerlessSource from './prix/sources/serverless'
import keycloak from './utils/client/keycloak'
import ApiWithContext from './components/apiWithContext'
import { KeycloakProvider } from './components/keycloak'
import { apiBasePath, isDevelopment, isDebugMobileOn } from './utils/shared/platform'
import { NotificationsProvider } from './packages/notifications/notificationContext'
import { ToastContainer } from 'react-toastify'
import { createRoot } from 'react-dom/client'
import { clear, clearOutdatedOnly } from './prix/utils/browserCache'
import initErrorReporting from './utils/client/errorReporting'
import { initializeAnalyticsClient } from './utils/client/analytics'

initErrorReporting()
initializeAnalyticsClient()

const serverlessSource = createServerlessSource({
  baseUrl: apiBasePath,
  entities,
  actionDefinitions,
})

const sources = {
  oltp: serverlessSource,
  olap: serverlessSource,
}

const appElement = document.getElementById('app')
const app = createRoot(appElement!)

function ErrorBoundaryFallback({ error }: { error: Error; resetErrorBoundary: () => void }) {
  async function handleTryAgain() {
    await clear().catch(error => console.error(error))
    await keycloak.logout({ redirectUri: window.location.origin }).catch(console.error)
  }

  return (
    <Handle
      error={error}
      onTryAgain={handleTryAgain}
    />
  )
}

app.render(
  <ThemeProvider theme={theme}>
    <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
      <KeycloakProvider keycloak={keycloak}>
        <ApiWithContext entities={entities} sources={sources}>
          <NotificationsProvider>
            <Router />
          </NotificationsProvider>
        </ApiWithContext>
      </KeycloakProvider>
    </ErrorBoundary>
    <ToastContainer />
  </ThemeProvider>,
)

async function prepareServiceWorker() {
  if ('serviceWorker' in navigator) {
    if (isDevelopment) {
      const registration = await navigator.serviceWorker.ready
      await registration.unregister()
      return
    }

    const { Workbox } = await import('workbox-window')
    const workbox = new Workbox('/sw.js')

    let shouldReloadOnControlling = false

    workbox.addEventListener('installed', event => {
      // First install: no reload. Updates: reload when it takes control.
      if (event.isUpdate) {
        shouldReloadOnControlling = true
      }
    })

    // Some older SW versions may still enter "waiting" (e.g. if they didn't skipWaiting).
    workbox.addEventListener('waiting', () => {
      shouldReloadOnControlling = true
      workbox.messageSkipWaiting()
    })

    workbox.addEventListener('controlling', () => {
      if (shouldReloadOnControlling) {
        window.location.reload()
      }
    })

    await workbox.register()

    // Force periodic update checks to reduce "next day" SW updates.
    setTimeout(() => workbox.update().catch(() => null), 30_000)
    window.addEventListener('focus', () => workbox.update().catch(() => null))
  }
}

async function loadMobileDebugger() {
  if (isDebugMobileOn && isDevelopment) {
    import('eruda')
      .then(eruda => {
        eruda.default.init()
        console.info('Eruda iniciado para debug mobile.')
      })
      .catch(error => {
        console.error('Erro ao carregar Eruda:', error)
      })
  }
}

prepareServiceWorker().catch(error => console.error(error))
loadMobileDebugger().catch(error => console.error(error))

// Clear outdated cache
async function clearOutdatedCacheLoop() {
  await clearOutdatedOnly().catch(error => console.error(error))
  setTimeout(
    () => {
      clearOutdatedCacheLoop()
    },
    4 * 60 * 60 * 1000, // 4 hours to avoid memory leaks
  )
}
setTimeout(clearOutdatedCacheLoop, 30 * 1000) // 30 seconds from start
